import type { Column } from '@/components/Organisms/DataTable/DataTable.types';
import type {
	DataExportRowTransform,
	FetchExportCsvMessage,
} from '@/types/dataExportWorker.types';
import type { CsvExportColumn } from '@/utils/csvExportCore';
import { exportDownloadTimestamp } from '@/utils/exportDownloadTimestamp';

function toSerializableColumns(columns: Column[]): CsvExportColumn[] {
	return columns.map((c) => ({
		key: c.key,
		label: c.label,
		skipExport: c.skipExport,
	}));
}

/** Fetch + JSON + row transform + CSV in a worker; main thread supplies signed URL/headers and triggers download. */
export function runFetchExportCsvWorker(options: {
	url: string;
	headers: Record<string, string>;
	itemsPath: string[];
	rowTransform: DataExportRowTransform;
	columns: Column[];
	docName: string;
}): Promise<void> {
	return new Promise((resolve, reject) => {
		const worker = new Worker(
			new URL('../workers/dataExport.worker.ts', import.meta.url),
			{ type: 'module' },
		);

		worker.onmessage = (ev: MessageEvent) => {
			const data = ev.data as
				| { type: 'done'; parts: string[] }
				| { type: 'error'; message: string };

			worker.terminate();

			if (data.type === 'error') {
				reject(new Error(data.message));
				return;
			}

			const BOM = '\uFEFF';
			const blob = new Blob([BOM, ...data.parts], {
				type: 'text/csv;charset=utf-8;',
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `${options.docName}-${exportDownloadTimestamp()}.csv`;
			link.click();
			URL.revokeObjectURL(url);
			resolve();
		};

		worker.onerror = (ev: ErrorEvent) => {
			worker.terminate();
			reject(ev.error ?? new Error(ev.message));
		};

		const payload: FetchExportCsvMessage = {
			type: 'fetch-export-csv',
			url: options.url,
			headers: options.headers,
			itemsPath: options.itemsPath,
			rowTransform: options.rowTransform,
			columns: toSerializableColumns(options.columns),
		};

		worker.postMessage(payload);
	});
}
